/**
 * Which events a resource trigger fires on: an empty object matches every event, a leaf narrows by
 * what changed (`diff`) or by related resources (`relationships`), and `and`, `or` and `not`
 * combine them.
 */
import { z } from 'zod';
// Each member is `.strict()` so a foreign key can't be silently stripped. An
// operator object carrying more than one key (`{ in: ['a'], eq: 'b' }`) is
// ambiguous and must fail the union rather than parse as whichever member
// happens to match first with the extra keys dropped.
export const ResourceTriggerComparisonOperatorSchema = z.union([
    z.object({ eq: z.unknown() }).strict(),
    z.object({ ne: z.unknown() }).strict(),
    z.object({ gt: z.unknown() }).strict(),
    z.object({ gte: z.unknown() }).strict(),
    z.object({ lt: z.unknown() }).strict(),
    z.object({ lte: z.unknown() }).strict(),
    z.object({ in: z.array(z.unknown()) }).strict(),
    z.object({ nin: z.array(z.unknown()) }).strict(),
    z.object({ exists: z.boolean() }).strict(),
    z.object({ contains: z.string() }).strict(),
]);
// `.strict()` so foreign keys can't be silently stripped into an empty
// transition when a malformed operator falls through the union.
const ResourceTriggerTransitionOperandSchema = z
    .object({
    from: z.unknown().optional(),
    to: z.unknown().optional(),
})
    .strict();
// Reject an empty operand (`{}`) at the union-value level, not inside the
// transition branch. Both `from` and `to` are optional, so the transition
// member accepts `{}` on its own and a branch-local refine would be bypassed
// by the union picking that member. An empty operand never matches at runtime
// (`evaluateDiff` falls through to `evaluateComparison({})` → `return false`).
export const ResourceTriggerDiffCriterionSchema = z.record(z.string(), z
    .union([ResourceTriggerComparisonOperatorSchema, ResourceTriggerTransitionOperandSchema])
    .refine((operand) => Object.keys(operand).length > 0, {
    message: 'Diff criterion operand must have at least one key',
}));
export const ResourceTriggerRelationshipCriterionSchema = z.record(z.string(), z.object({
    where: z.record(z.string(), ResourceTriggerComparisonOperatorSchema),
}));
const ResourceTriggerLeafCriteriaSchema = z
    .object({
    diff: ResourceTriggerDiffCriterionSchema.optional(),
    relationships: ResourceTriggerRelationshipCriterionSchema.optional(),
})
    .strict()
    .refine((data) => data.diff ?? data.relationships ?? Object.keys(data).length === 0, {
    message: "Criteria must be an empty object {} (match all) or contain at least one of 'diff' or 'relationships'",
});
export const ResourceTriggerCriteriaSchema = z.lazy(() => z.union([
    z.object({ and: z.array(ResourceTriggerCriteriaSchema) }).strict(),
    z.object({ or: z.array(ResourceTriggerCriteriaSchema) }).strict(),
    z.object({ not: ResourceTriggerCriteriaSchema }).strict(),
    ResourceTriggerLeafCriteriaSchema,
]));
export function isResourceTriggerAndCriteria(c) {
    return 'and' in c;
}
export function isResourceTriggerOrCriteria(c) {
    return 'or' in c;
}
export function isResourceTriggerNotCriteria(c) {
    return 'not' in c;
}
export function isResourceTriggerLeafCriteria(c) {
    return (!isResourceTriggerAndCriteria(c) &&
        !isResourceTriggerOrCriteria(c) &&
        !isResourceTriggerNotCriteria(c));
}
//# sourceMappingURL=criteria.js.map